import React from "react";

import {carsCommonUrl} from "../../../app/utils/read_environment_variables";
import {engineTypes, getLabelByValue} from "../../../dashboard/add_car/options/select_options";
import {getSellerUrl} from "../../utils/get_seller_slug";
import {ICarDetailResponse} from "../actions/fetch_cars_details_at_route";
import {buildOfferCanonicalUrl, getOfferSeoImage} from "../utils/build_offer_seo";

type CarDetail = ICarDetailResponse["data"];

interface IProps {
    car: CarDetail;
}

const toNumber = (value: string | number | undefined): number | undefined => {
    if (value === undefined || value === null || value === "") {
        return undefined;
    }
    const digits = String(value).replace(/\D/g, "");
    if (!digits) {
        return undefined;
    }
    return Number(digits);
};

export const SchemaCarOffer = ({car}: IProps) => {
    if (!car?.id || !car.slug) {
        return null;
    }

    const acf = car.acf || ({} as CarDetail["acf"]);
    const url = buildOfferCanonicalUrl(car.slug);
    const image = getOfferSeoImage(car);
    const fuel = getLabelByValue(acf.engine, engineTypes) || acf.engine;
    const price = toNumber(acf.price);
    const mileage = toNumber(acf.millage);
    const year = toNumber(acf.year);
    const power = toNumber(acf.horse_power);
    const engineSize = toNumber(acf.engine_size);
    const sellerPath = acf.sellers_name ? getSellerUrl(acf.sellers_name, car.seller_slug) : null;

    const breadcrumbItems = [
        {name: "Strona główna", item: `${carsCommonUrl}/`},
        {name: "Osobowe", item: `${carsCommonUrl}/osobowe/`},
        acf.make
            ? {
                  name: acf.make,
                  item: `${carsCommonUrl}/osobowe/${encodeURIComponent(String(acf.make).toLowerCase())}/`
              }
            : null,
        {name: car.title || [acf.make, acf.model].filter(Boolean).join(" "), item: url}
    ].filter(Boolean) as Array<{name: string; item: string}>;

    const vehicle: Record<string, unknown> = {
        "@type": "Car",
        "@id": `${url}#vehicle`,
        name: car.title,
        url,
        description:
            (car.content || "")
                .replace(/<[^>]+>/g, " ")
                .replace(/\s+/g, " ")
                .trim()
                .slice(0, 300) || undefined,
        brand: acf.make
            ? {
                  "@type": "Brand",
                  name: acf.make
              }
            : undefined,
        model: acf.model || undefined,
        vehicleModelDate: year || undefined,
        mileageFromOdometer: mileage
            ? {
                  "@type": "QuantitativeValue",
                  value: mileage,
                  unitCode: "KMT"
              }
            : undefined,
        vehicleEngine:
            fuel || power || engineSize
                ? {
                      "@type": "EngineSpecification",
                      name: [fuel, power ? `${power} KM` : null, engineSize ? `${engineSize} cm³` : null].filter(Boolean).join(", ")
                  }
                : undefined,
        bodyType: acf.body_type || undefined,
        vehicleTransmission: acf.gearbox || undefined,
        color: car.details_basic?.car_color || undefined,
        image: image ? [image] : undefined,
        offers: price
            ? {
                  "@type": "Offer",
                  url,
                  priceCurrency: "PLN",
                  price: String(price),
                  availability: "https://schema.org/InStock",
                  itemCondition: "https://schema.org/UsedCondition",
                  seller: acf.sellers_name
                      ? {
                            "@type": "Organization",
                            name: acf.sellers_name,
                            ...(sellerPath ? {url: `${carsCommonUrl}${sellerPath}`} : {})
                        }
                      : {
                            "@type": "Organization",
                            name: "Zakupauto"
                        }
              }
            : undefined
    };

    Object.keys(vehicle).forEach((key) => {
        if (vehicle[key] === undefined) {
            delete vehicle[key];
        }
    });

    const json = {
        "@context": "https://schema.org",
        "@graph": [
            vehicle,
            {
                "@type": "BreadcrumbList",
                itemListElement: breadcrumbItems.map((item, index) => ({
                    "@type": "ListItem",
                    position: index + 1,
                    name: item.name,
                    item: item.item
                }))
            }
        ]
    };

    return <script type="application/ld+json" dangerouslySetInnerHTML={{__html: JSON.stringify(json)}} />;
};
